import {useContext, useEffect, useMemo, useState} from 'react';

import {actions, PermissionToCheck} from '../../../../enums';
import {checkPermissions} from '../../../../helpers/permissionsHtml';
import {useService} from '../../../../hooks';
import {useUser} from '../../../../hooks/services/useUser';
import {ProcessTree, Unit} from '../../../../models';
import {EditProcessVersion} from '../../../../models/EditProcessVersion';
import {AuthenticationContext} from '../../../../providers/AuthenticationContext';
import {ApiResponse} from '../../../../services/ApiResponseType';
import {useUnit} from '../../../../hooks/services/useUnit';
import {IDropdownOption} from '@fluentui/react';
import {sortByTitle} from '../../../../helpers/arrayMehods';
import {UserContext} from '../../../../providers';

export const useEdit = (processId: number, unitId: number) => {
  const {isLoading: isAzureLoading} = useContext(AuthenticationContext);
  const {getProcessVersionsDraft, getUserById, getMenuProcessesPublishedForUnit, getUnit} =
    useService();
  const {getUserPermissionsPage} = useUser();
  const {isUserContributor, isUserAdmin} = useContext(UserContext);
  const {getUnitsImplementingProcess} = useUnit();
  const [publishedProcessesForUnit, setPublishedProcessesForUnit] =
    useState<ApiResponse<ProcessTree[]>>();
  const [unitDetails, setUnitDetails] = useState<ApiResponse<Unit>>();
  const [unitsListForParentProcess, setUnitsListForParentProcess] = useState<
    IDropdownOption<number>[]
  >([]);
  useEffect(() => {
    (async () => {
      const processList = await getMenuProcessesPublishedForUnit(unitId);
      setPublishedProcessesForUnit(processList);
      const unit = await getUnit(unitId);
      setUnitDetails(unit);
    })();
    /* eslint-disable*/
  }, [getMenuProcessesPublishedForUnit, unitId, setPublishedProcessesForUnit]);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [processData, setProcessData] = useState<ApiResponse<EditProcessVersion>>();
  const [processOwner, setProcessOwner] =
    useState<{id: string; text: string; key: string; secondaryText: string}[]>();
  const [secondaryProcessOwners, setSecondaryProcessOwners] =
    useState<{id: string; text: string; key: string; secondaryText: string}[]>();
  secondaryProcessOwners;
  const [canSelectProcessOwner, setCanSelectProcessOwner] = useState(false);
  const [canChangeProcessParent, setCanChangeProcessParent] = useState(false);

  const updateProcessVersionsDraft = useMemo(
    () => async () => {
      setIsLoading(true);
      if (!isAzureLoading) {
        const process = await getProcessVersionsDraft(processId);
        setProcessData(process);
        setIsLoading(false);
      }
    },
    [isAzureLoading, processId, getProcessVersionsDraft]
  );
  useEffect(() => {
    updateProcessVersionsDraft();
  }, [updateProcessVersionsDraft]);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      let canSelectParentProcess: boolean = false;
      const response = await getUserPermissionsPage(unitId, processId);
      const hasGlobalPermission = isUserContributor || isUserAdmin;
      if (response?.result) {
        const canStillAccess = checkPermissions(
          PermissionToCheck.ChangeProcessOwner,
          actions.UPDATE,
          response.result
        );
        setCanSelectProcessOwner(canStillAccess || hasGlobalPermission);
        canSelectParentProcess = checkPermissions(
          PermissionToCheck.EditProcessParent,
          actions.UPDATE,
          response.result
        );
        setCanChangeProcessParent(canSelectParentProcess || hasGlobalPermission);
      } else {
        setCanSelectProcessOwner(false);
        setCanChangeProcessParent(false);
      }
      if (processData?.result) {
        const unitsList = await getUnitsImplementingProcess(
          unitId,
          processData.result.masterProcessId
        );
        if (unitsList.result)
          setUnitsListForParentProcess(
            unitsList.result.sort(sortByTitle).map(unit => ({
              key: unit.unitId,
              text: `${unit.unitTitle || ''}${unit.canNotBeImplemented ? '*' : ''}`,
              data: unit.id,
              disabled: unit.canNotBeImplemented
            }))
          );

        if (processData.result?.processOwner) {
          const user = processData.result?.processOwner.azureId
            ? await getUserById(processData.result?.processOwner.azureId)
            : undefined;
          const person = user
            ? [
                {
                  id: user.id,
                  text: user.displayName,
                  secondaryText: user.mail,
                  key: user.id
                }
              ]
            : undefined;
          setProcessOwner(person);
        } else {
          setProcessOwner(undefined);
        }
        if (
          processData.result?.secondaryProcessOwners &&
          processData.result?.secondaryProcessOwners.length > 0
        ) {
          const owners = processData.result?.secondaryProcessOwners;
          const users = await Promise.all(
            owners.filter(owner => owner?.azureId).map(owner => getUserById(owner.azureId))
          );

          const persons = users
            .filter((u): u is NonNullable<typeof u> => u !== undefined)
            .map(user => ({
              id: user.id,
              text: user.displayName,
              secondaryText: user.mail,
              key: user.id
            }));
          setSecondaryProcessOwners(persons);
        } else {
          setSecondaryProcessOwners([]);
        }

        
      }

      setIsLoading(false);
    })();
  }, [processData, getUserById]);

  return {
    isLoading,
    processData,
    unitsListForParentProcess,
    processOwner,
    secondaryProcessOwners,
    publishedProcessesForUnit,
    canSelectProcessOwner,
    canChangeProcessParent,
    updateProcessVersionsDraft,
    unitDetails
  };
};
